import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  acceptCompanyClaimInvitation,
  fetchCompanyPayoutDetails,
  fetchMyCompany,
  previewCompanyClaimInvitation,
} from '../services/api/companyOwnerAuth';
import {
  clearCompanyOwnerClaimToken,
  getCompanyOwnerAuthUser,
  getCompanyOwnerClaimToken,
  saveCompanyOwnerCompany,
  saveCompanyOwnerPayoutDetails,
} from '../services/storage/companyOwnerFlow';
import {
  formatDateTime,
  getCompanyOwnerNextRoute,
  getNonCompanyAccessFallbackRoute,
  isCompanyOwnerUser,
} from '../utils/companyOwner';
import type { CompanyClaimInvitationPreview } from '../types/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';

const CompanyOwnerOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const companyOwnerUser = useMemo(() => getCompanyOwnerAuthUser(), []);
  const [checking, setChecking] = useState(true);
  const [claimToken, setClaimToken] = useState(getCompanyOwnerClaimToken() || '');
  const [claimPreview, setClaimPreview] = useState<CompanyClaimInvitationPreview | null>(null);
  const [claimPreviewError, setClaimPreviewError] = useState('');
  const [claimActionError, setClaimActionError] = useState('');
  const [claimActionLoading, setClaimActionLoading] = useState(false);

  useEffect(() => {
    if (!isCompanyOwnerUser(companyOwnerUser)) {
      navigate(getNonCompanyAccessFallbackRoute(), { replace: true });
      return;
    }

    let mounted = true;

    const bootstrap = async () => {
      try {
        const existingCompany = await fetchMyCompany();
        if (!mounted) return;

        if (existingCompany) {
          saveCompanyOwnerCompany(existingCompany);
          clearCompanyOwnerClaimToken();
          try {
            const payoutDetails = await fetchCompanyPayoutDetails();
            if (!mounted) return;
            saveCompanyOwnerPayoutDetails(payoutDetails);
            navigate(getCompanyOwnerNextRoute(existingCompany, payoutDetails), { replace: true });
          } catch {
            navigate(getCompanyOwnerNextRoute(existingCompany), { replace: true });
          }
          return;
        }

        if (!claimToken) {
          setChecking(false);
          return;
        }

        const preview = await previewCompanyClaimInvitation(claimToken);
        if (!mounted) return;
        setClaimPreview(preview);
      } catch (error: any) {
        if (!mounted) return;
        if (claimToken) {
          setClaimPreviewError(error.message || 'Could not load the invitation preview.');
        }
      } finally {
        if (mounted) {
          setChecking(false);
        }
      }
    };

    void bootstrap();

    return () => {
      mounted = false;
    };
  }, [claimToken, companyOwnerUser, navigate]);

  const handleAcceptInvitation = async () => {
    if (!claimToken) {
      setClaimActionError('No company invitation link was found for this account.');
      return;
    }

    setClaimActionLoading(true);
    setClaimActionError('');

    try {
      const company = await acceptCompanyClaimInvitation(claimToken);
      saveCompanyOwnerCompany(company);
      clearCompanyOwnerClaimToken();
      setClaimToken('');
      try {
        const payoutDetails = await fetchCompanyPayoutDetails();
        saveCompanyOwnerPayoutDetails(payoutDetails);
        navigate(getCompanyOwnerNextRoute(company, payoutDetails), { replace: true });
      } catch {
        navigate(getCompanyOwnerNextRoute(company), { replace: true });
      }
    } catch (error: any) {
      setClaimActionError(error.message || 'Could not accept the company invitation.');
    } finally {
      setClaimActionLoading(false);
    }
  };

  if (checking) {
    return (
      <section className="register-page">
        <div className="register-shell register-shell-single">
          <div className="register-panel register-panel-form register-status-panel">
            <div className="register-loader" />
            <h1 className="register-form-title">Preparing company access</h1>
            <p className="register-form-copy">
              We are checking whether this company-owner account already has a linked company profile.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="register-page">
      <div className="register-shell register-shell-single">
        <aside className="register-panel register-panel-brand">
          <div className="register-brand-lockup">
            <div className="register-brand-badge">Company Claim</div>
            <h1 className="register-brand-title">
              Company access now starts from a driver-seeded invitation.
            </h1>
            <p className="register-brand-copy">
              Company profiles are no longer created from public form details. A driver must onboard
              first and send a one-time claim invitation to your company email.
            </p>
          </div>

          <div className="register-benefit-list">
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>The invited company email must match the company-owner account used here.</span>
            </div>
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>Invitation links are one-time use and expire automatically.</span>
            </div>
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>Once accepted, your dashboard and linked driver visibility work as normal.</span>
            </div>
          </div>
        </aside>

        <div className="register-panel register-panel-form">
          <div className="register-form-header">
            <div className="register-form-badge">Company Owner Onboarding</div>
            <h1 className="register-form-title">
              {claimToken ? 'Finish claiming your invited company' : 'No linked company yet'}
            </h1>
            <p className="register-form-copy">
              {claimToken
                ? 'Review the invitation details below and finish the company claim for this authenticated account.'
                : 'This company-owner account is valid, but no company invitation has been accepted yet.'}
            </p>
          </div>

          {claimToken && claimPreview ? (
            <div className="register-highlight-card">
              <div className="register-highlight-row">
                <span className="register-highlight-icon">C</span>
                <div>
                  <div className="register-highlight-title">Invitation Ready</div>
                  <div className="register-highlight-note">{claimPreview.companyName}</div>
                </div>
              </div>
              <p className="register-highlight-copy">
                You&apos;ve been invited to claim {claimPreview.companyName} on ShiftWagon.
              </p>
              <div className="register-benefit-list" style={{ marginTop: '18px' }}>
                <div className="register-benefit-item">
                  <span className="register-benefit-dot" />
                  <span>Invited email: {claimPreview.maskedCompanyEmail}</span>
                </div>
                <div className="register-benefit-item">
                  <span className="register-benefit-dot" />
                  <span>
                    Expires: {claimPreview.expiresAt ? formatDateTime(claimPreview.expiresAt) : 'Not available'}
                  </span>
                </div>
                {claimPreview.invitedBy ? (
                  <div className="register-benefit-item">
                    <span className="register-benefit-dot" />
                    <span>Invited by: {claimPreview.invitedBy}</span>
                  </div>
                ) : null}
              </div>
            </div>
          ) : claimToken && claimPreviewError ? (
            <div className="register-inline-error">{claimPreviewError}</div>
          ) : (
            <div className="register-highlight-card">
              <p className="register-highlight-copy">
                Ask one of your company drivers to complete company-driver onboarding with your
                company email address. ShiftWagon will send a secure claim link to that inbox.
              </p>
            </div>
          )}

          {claimActionError ? <div className="register-inline-error">{claimActionError}</div> : null}

          <div className="register-flow-actions">
            <Link className="register-secondary-button" to="/">
              Go Home
            </Link>
            {claimToken ? (
              <button
                className="register-submit register-submit-inline"
                disabled={claimActionLoading || Boolean(claimPreviewError)}
                onClick={handleAcceptInvitation}
                type="button"
              >
                {claimActionLoading ? 'Accepting invitation...' : 'Claim invited company'}
              </button>
            ) : (
              <Link className="register-submit register-submit-inline" to="/login?role=company">
                Company Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyOwnerOnboardingPage;
